import * as v from 'valibot';

/**
 * API envelope format definition
 */
export interface Envelope<T> {
  data: T;
}

/**
 * Text Content entity
 */
export interface TextContent {
  id: number;
  content: string;
}
export interface SourceType {
  type: 'course' | 'element' | 'module';
  id: string;
  child?: SourceType;
}

/**
 * Learner Progression Text Progression request payload
 */
export interface TextProgressionPayload {
  data: {
    id: string;
    actions: 'loaded';
  };
  source: SourceType;
}

/**
 * Audio Content/Progression
 */
export interface AudioContent {
  id: string;
  url: string;
  downloadable: boolean;
  'download-url': string;
}

export interface AudioProgression {
  'duration-in-seconds': number;
}

export interface AudioProgressionPayload {
  data: {
    id: string;
    'duration-in-seconds': number;
    action: string;
  };
  source: SourceType;
}

/**
 * Document Content/Progression
 */
export interface DocumentContent {
  id: string;
  url: string;
}

export interface DocumentProgression {
  page_number: number;
}

export interface DocumentProgressionPayload {
  data: {
    id: string;
    page_number: number | string;
    action: string;
  };
  source: SourceType;
}

export interface VideoContent {
  id: string;
  url: string;
  caption: string;
  thumbnail: string;
  tracking: boolean;
  downloadable: boolean;
  'download-url': string;
  'required-viewed-percentage': number;
}

export interface VideoProgression {
  'maximum-position-duration-seconds': number;
  'duration-in-seconds': number;
  'current-position-duration-seconds': number;
}

export interface VideoProgressionPayload {
  data: {
    id: string;
    'duration-in-seconds': number;
    action: string;
  };
  source: SourceType;
}

export interface Bookmark {
  type: 'module' | 'element';
  id: string;
}

export interface PageProgression {
  id: string;
  status: string;
  percentage_completion: number;
  elements: unknown[];
  bookmark: Bookmark;
}

export interface PageProgressionPayload {
  data: {
    id: string;
    courseId: string;
    moduleId: string;
  };
}

/**
 * Set GUIDs used to identify a content resource
 */
export interface ContentGUID {
  /** Text GUID */
  id: string;
  /** Current course GUID */
  courseId: string;
  /** Current module GUID */
  moduleId: string;
  /** Element GUID */
  elementId: string;
}

export interface EntryComponentConfig {
  baseUrl?: string;
  flags?: {
    [key: string]: boolean;
    isPortal?: boolean;
    isPrerenderEnabled?: boolean;
    ignoreScormCheck?: boolean;
  };
  reattemptNewFlow?: boolean;

  // AI Smart Search Configuration
  aiApiEndpoint?: string;
  aiApiTimeout?: number; // in ms
}

export enum ENROLLMENT_STATUSES {
  WAITLISTED = -4,
  PARTIALLY_ATTENDED = -3,
  CANCELLED = -2,
  NO_SHOW = -1,
  NOT_STARTED = 1,
  IN_PROGRESS = 2,
  COMPLETED = 3,
  PASSED = 4,
  FAILED = 5,
  PENDING_REVIEW = 6,
  CLOSED = 7,
  PAST_DUE = 8,
}

export type EnrollmentStatusKeys = keyof typeof ENROLLMENT_STATUSES;

export const EnrollmentComponentSchema = v.object({
  checklist_data: v.optional(
    v.object({
      status: v.number(),
      enrollment_component_checklist_id: v.number(),
      checklist_name: v.string(),
      checklist_owner_user_id: v.number(),
      questions: v.array(v.unknown()),
    })
  ),
  component_id: v.number(),
  component_type_id: v.number(),
  enrollment_component_status_id: v.enum(ENROLLMENT_STATUSES),
  enrollment_id: v.number(),
  id: v.number(),
  title: v.string(),
  is_survey: v.nullish(v.union([v.literal(0), v.literal(1)])),
  survey_is_mandatory: v.nullish(v.union([v.literal(0), v.literal(1)])),
  is_knowledge_test: v.nullish(v.union([v.literal(0), v.literal(1)])),
  was_rejected: v.nullish(v.union([v.literal(0), v.literal(1)])),
  override_sequencing: v.nullish(v.boolean()),
  enrollment_exam_status_id: v.nullish(v.number()),
  release_at: v.nullish(v.string()),
  description: v.nullish(v.string()),
  portal_id: v.number(),
  owner_group_id: v.nullish(v.number()),
  group_id: v.nullish(v.number()),
  sequence: v.nullish(v.number()),
  attendence_set_at: v.nullish(v.union([v.string(), v.null()])),
  updated_at: v.nullish(v.string()), // TODO: make this a Date
  enrollment_status_id: v.string(),
  date_completed: v.nullish(v.string()),
  date_started: v.nullish(v.string()),
  number_enrolled_on_session: v.nullish(v.number()),
  max_capacity: v.nullish(v.union([v.number(), v.null()])),
  enable_waitlist: v.nullish(v.number()),
  session_percentage: v.nullish(v.union([v.number(), v.null()])),
  is_tin_can: v.nullish(v.number()),
  location_id: v.nullish(v.union([v.number(), v.null()])),
  percentage: v.nullish(v.union([v.number(), v.null()])),
  mastery_score: v.nullish(v.union([v.number(), v.null()])),
  use_crocodoc_js_viewer: v.nullish(v.number()),
  start_at: v.nullish(v.union([v.string(), v.null()])),
  end_at: v.nullish(v.union([v.string(), v.null()])),
  user_id: v.number(),
  show_pass_fail_after_submit: v.nullish(v.union([v.boolean(), v.number(), v.null()])),
  assignment_answer_percentage: v.nullish(v.union([v.number(), v.null()])),
  assignment_answer_id: v.nullish(v.union([v.number(), v.null()])),
  course_id: v.nullish(v.number()),
  minimum_attendance_threshold: v.nullish(v.union([v.number(), v.null()])),
  set_by_mark_complete: v.nullish(v.boolean()),
  enrollment_component_element_id: v.nullish(v.union([v.number(), v.null()])),
  max_page_viewed: v.nullish(v.union([v.number(), v.null()])),
  num_pages: v.nullish(v.union([v.number(), v.null()])),
  video_duration: v.nullish(v.union([v.number(), v.null()])),
  video_duration_viewed: v.nullish(v.union([v.number(), v.null()])),
  video_last_position: v.nullish(v.union([v.number(), v.null()])),
  max_video_position_viewed: v.nullish(v.union([v.number(), v.null()])),
  strict_loading_mode: v.nullish(v.string()),
  show_reattempt_button: v.nullish(v.boolean()),
});

export type EnrollmentComponent = v.InferOutput<typeof EnrollmentComponentSchema>;

export type Module = EnrollmentComponent;

export interface Enrollment {
  allow_learner_to_uneneroll: number;
  allow_relaunch_after_completion: number;
  allow_users_rate_course: number;
  auto_launch_scorm: number;
  award_credits_per_session: number;
  bypass_sequencing_on_assignments: number;
  bypass_sequencing_on_checklists: number;
  bypass_sequencing_on_esignatures: number;
  calculated_due_date: null;
  can_review_courses: number;
  certification_id: number | null;
  certification_title: null;
  co_email: string;
  co_first_name: string;
  co_id: number;
  co_last_name: string;
  co_username: string;
  contain_rejected_assignments: boolean;
  contains_assignments: number;
  contains_sessions: number;
  contains_tincans: number;
  contains_trainings: number;
  course_certification_id: null;
  course_enable_waitlist: number;
  course_id: number;
  course_guid: string;
  course_length_unit: number;
  course_tutor_name: string;
  course_waitlist_type: number;
  course_was_rated: boolean;
  cr_must_choose_session: number;
  created_at: string;
  date_after_enrollment: null;
  date_completed: null;
  date_lastaccessed: string;
  date_published: string;
  date_started: string;
  days_after_enrollment: null;
  description: string;
  difficulty_level: number;
  disable_message_author: null;
  disable_questions: number;
  enable_sequencing: number;
  expires_at: null;
  group_id: null;
  has_attempts_remaining: boolean;
  has_expired: number;
  hide_release_dates: number;
  id: number;
  is_perpetual: null;
  last_accessed_enrollment_component_id: number;
  launch_to_description: number;
  learning_path_do_course_certs: null;
  learning_path_do_course_credits: null;
  learning_path_id: number;
  learning_path_name: string;
  license_date_expires: null;
  license_id: null;
  lp_published_status_id: null;
  minute_length: null;
  must_choose_session: boolean;
  my_star_rating: string;
  my_star_rating_id: null;
  name: string;
  num_modules: number;
  number_of_rates: number;
  number_of_sessions: number;
  number_scoreables: number;
  objectives: null;
  only_contains_sessions: number;
  owner_group_id: number;
  pass_mark: null;
  percentage: number | null;
  percentage_complete: number;
  personal_tutor_name: null;
  pm_id: number;
  portal_id: number;
  review_is_mandatory: number;
  sales_force_id: number | null;
  shared_linkedin: number;
  show_passfail_to_learners: number;
  show_scores_to_learners: number;
  small_description: string;
  star_sum: number;
  status_id: ENROLLMENT_STATUSES;
  stick_to_dash: number;
  strict_loading_mode: string;
  tc_check_rating: boolean;
  tutor_email: string | null;
  tutor_first_name: string | null;
  tutor_id: number | null;
  tutor_last_name: string | null;
  tutor_username: string | null;
  user_id: number;
}

export interface PreloadSettings {
  disable_messaging: boolean;
  enable_module_release_dates: boolean;
  catalog_enabled: boolean;
  ecommerce_enabled: boolean;
  can_create_assignments: boolean;
  enable_ilts: boolean;
  trainings_enabled: boolean;
  current_portal_id: number;
  enable_course_ref_nums: boolean;
  enable_associations_mode: boolean;
  have_membership_types_enabled: boolean;
  usernames_enabled: boolean;
  white_labelled_request: boolean;
  request_on_learnupon_domain: boolean;
  message_filters: {
    read: string;
    unread: string;
    important: string;
    unimportant: string;
    sent: string;
    received: string;
  };
  unread_messages: number;
  dashboard_filters: {
    enrolled_courses: number;
    completed_courses: number;
    learning_paths: number;
    pending_review: number;
    external_trainings: number;
    pending_enrollments: number;
    course_questions: number;
    enrollment_requests: number;
    learning_journeys: number;
  };
  hide_release_dates: boolean;
  cdn_path: string;
  currency_symbol: string;
  available_languages: {
    locale_id: number;
    locale_code: string;
    locale_name: string;
  }[];
  current_controller: string;
  password_policy_config: {
    is_enabled: boolean;
    enable_password_expiration: boolean;
    password_expiration_days: number;
    regex_validator: {
      name: string;
      form_label: {
        order: number;
        translation_key: string;
        translation_options: {
          num: number;
        };
      };
      error_msg: {
        translation_key: string;
        translation_options: {
          num: number;
        };
      };
      regex: string;
    }[];
    portal_id: number;
    retention_config: {
      is_enabled: boolean;
      number_to_keep: number;
    };
  };
  allow_profile_images: boolean;
  forget_me_enabled: number;
  email_regex: string;
  domain_regex: string;
  learning_paths_enabled: boolean;
  user_allowed_to_copy_courses: boolean;
  skills_enabled: boolean;
}

export interface PreloadResource {
  awarded_badges: {
    data: Array<{
      id: number;
      portal_id: number;
      name: string;
      is_used: boolean;
      image_file_name: string | null;
      image_file_size: number | null;
      image_content_type: string | null;
      default_image_path: string;
      points: number;
      badge_type_id: number;
      created_at: string;
      updated_at: string;
      created_by_pm_id: number;
      updated_by_pm_id: number;
      alt_tag: string | null;
      strict_loading_mode: string;
      image_url: string;
    }>;
    meta: unknown | null;
  };
  awarded_skills: {
    data: Array<{
      guid: string;
      name: string;
      description: string;
    }>;
  };
  next_course_on_path_id: number;
  on_description_page: boolean;
  is_preview: boolean;
  enrollment: {
    attributes: Enrollment;
  };
  enable_module_release_dates: boolean;
  enrollment_component: EnrollmentComponent | { attributes: EnrollmentComponent };
  enrollment_components: {
    data: Array<EnrollmentComponent>;
  };
  flags?: {
    attributes?: Flags;
  };
  was_just_completed: boolean | null;
  social_sharing?: {
    attributes: SocialSharing;
  } | null;
  hide_module_sidebar: boolean;
  embedded_content: boolean;
  embedded_content_autoplay: boolean;
  tincan_completion_tracking: boolean;
  content_details_redesign_enabled: boolean;
  concurrent_and_series_sessions: boolean;
}

export const FlagsSchema = v.partial(
  v.object({
    enrollment_component_status_id: v.nullish(v.number()),
    exam_question_index: v.nullish(v.number()),
    is_last_page_in_course: v.nullish(v.boolean()),
    last_page_confirmed_completed: v.nullish(v.boolean()),
    num_on_waitlist: v.nullish(v.number()),
    show_course_completion_button: v.nullish(v.boolean()),
    show_course_completion_immediately: v.nullish(v.boolean()),
    show_sign_button_immediately: v.nullish(v.boolean()),
  })
);

export type Flags = v.InferOutput<typeof FlagsSchema>;

export const SocialSharingSchema = v.object({
  linkedin_is_enabled: v.nullish(v.boolean()),
  social_sharing_enabled: v.nullish(v.number()),
  twitter_hash_tags: v.nullish(v.string()),
  twitter_is_enabled: v.nullish(v.boolean()),
  twitter_related_accounts: v.nullish(v.string()),
  twitter_url: v.nullish(v.string()),
});

export type SocialSharing = v.InferOutput<typeof SocialSharingSchema>;

export interface ScormContent {
  id: string;
  completion_threshold: number | null;
  component_title: string;
  component_background_img: string;
  component_description: string;
  content_href: string;
  data_from_lms: string;
  launch_scorm_in_new_window_ie11: boolean;
  mastery_score: number;
  max_time_allowed: string;
  only_overwrite_if_better: boolean;
  overwrite_previous_attempt: boolean;
  parameters: string;
  release_date: string;
  released: boolean;
  scorm_apiendpoint: string;
  scorm_content_location: string;
  scorm_popup_window_height: string;
  scorm_popup_window_width: string;
  scorm_version: string;
  time_limit_action: string;
  suspend_data_gap_enabled: boolean;
}

export interface ScormProgressionPayload {
  data: {
    id: string;
    courseId: string;
    attribute: string;
  };
}

export enum ComponentTypes {
  exam = 1,
  scorm = 2,
  page = 3,
  assignment = 4,
  session = 5,
  training = 6,
  checklist = 7,
  linkedin_learning = 8,
  tin_can = 9,
  e_signature = 10,
  survey = 101,
}

export enum KeyboardKeyCodes {
  Enter = 13,
  Space = 32,
}

export enum UserRoleCode {
  EXPIRED = -1,
  DISABLED = 0,
  MEMBER = 1,
  ADMIN = 2,
  INSTRUCTOR = 3,
  MANAGER = 4,
}
